// SEP-2 / E0 — StateTransitionBar: a category-agnostic lifecycle-transition control. Given a shared
// StatusLifecycle + the row's current status, it DERIVES the owner's next-state buttons from
// allowedNext(lifecycle, status, actor) — never a hard-coded per-status map — so it can never drift
// from the server's transition rules and lights up orders (placed→…→completed), inventory
// (in_stock↔low↔out→discontinued), deals stages, bookings, tickets, from one component.
//
// a11y: every transition is a real <button> whose aria-label names the target state + the entity, so a
// screen-reader user hears "Mark <entity> Ready", not a bare "Ready". Danger targets (cancel /
// discontinue) get danger styling AND still carry a full label. Direction-implying chevrons mirror
// under [dir=rtl] via the logical `ChevronRight` (the runtime flips it) — text carries the meaning, so
// the control is never color/icon-only.
import type { JSX } from 'preact';
import { ChevronRight, Loader2 } from 'lucide-preact';
import { allowedNext } from '@vibeshell/shared';
import type { StatusLifecycle, LifecycleActor } from '@vibeshell/shared';
import { t } from '../lib/i18n';

export interface StateTransitionBarProps {
  /** the resolved lifecycle (null → nothing renders; a lifecycle-less collection has no transitions). */
  lifecycle: StatusLifecycle | null;
  /** the row's current status id. */
  status: string;
  /** whose transitions to offer (default 'owner'). */
  actor?: LifecycleActor;
  /** disable all buttons while a mutation is in flight. */
  busy?: boolean;
  /** invoked with the target state id when the owner picks a transition. */
  onTransition: (to: string) => void;
  /** the entity's human name, woven into each button's aria-label ("Mark {entity} {state}"). */
  entityLabel?: string;
  /** state ids that should render with danger styling (e.g. 'cancelled', 'discontinued'). */
  dangerStates?: readonly string[];
  /** i18n key prefix for per-state labels (`<prefix>.<stateId>`); falls back to the lifecycle's
   *  ownerLabel, then the raw id. Mirrors ManageRecords' stateLabel so labels are consistent. */
  i18nPrefix?: string;
}

/** A state's owner-facing label: i18n `<prefix>.<id>` when present, else the lifecycle ownerLabel,
 *  else the raw id. NEVER a hard-coded status map. */
export function transitionStateLabel(lifecycle: StatusLifecycle | null, id: string, i18nPrefix?: string): string {
  if (i18nPrefix) {
    const key = `${i18nPrefix}.${id}`;
    const s = t(key);
    if (s !== key) return s;
  }
  const st = lifecycle?.states.find((x) => x.id === id);
  return st?.ownerLabel || id;
}

export function StateTransitionBar({
  lifecycle, status, actor = 'owner', busy, onTransition, entityLabel, dangerStates = [], i18nPrefix,
}: StateTransitionBarProps): JSX.Element | null {
  if (!lifecycle) return null;
  const next = allowedNext(lifecycle, status, actor);
  if (next.length === 0) return null;
  return (
    <div className="pf-row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-start' }} role="group" aria-label={t('own.transitionsAria')}>
      {busy && <Loader2 size={13} className="pf-spin" aria-hidden="true" />}
      {next.map((to) => {
        const label = transitionStateLabel(lifecycle, to, i18nPrefix);
        const danger = dangerStates.includes(to);
        return (
          <button
            key={to}
            type="button"
            className={`pf-btn pf-btn--sm${danger ? ' pf-btn--danger' : ' pf-btn--soft'}`}
            disabled={busy}
            aria-label={t('own.transitionTo', { entity: entityLabel ?? t('own.thisItem'), state: label })}
            onClick={() => onTransition(to)}
          >
            <ChevronRight size={12} aria-hidden="true" /> {label}
          </button>
        );
      })}
    </div>
  );
}
